import React, { useState } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Alert,
  Collapse,
  Badge,
} from 'reactstrap';
import classnames from 'classnames';
import { toast } from 'react-toastify';
import { reportsAPI, projectsAPI } from '../../helpers/api';
import { useIsMobile } from '../../helpers/useMediaQuery';

interface WaybillReport {
  total: number;
  matched: number;
  pending: number;
  notArrived: number;
  scanned: number;
  matchRate: number;
}

interface WageReport {
  personnel: Array<{
    id: string;
    firstName: string;
    lastName: string;
    title: string;
    totalDays: number;
    totalWage: number;
    entries: Array<{
      date: string;
      siteName: string;
      dailyWage: number;
    }>;
  }>;
  totalWage: number;
  totalDays: number;
}

interface DailyReport {
  records: Array<{
    id: string;
    date: string;
    siteName: string;
    personnelCount: number;
    vehicleCount: number;
    workCount: number;
    createdBy: string;
  }>;
}

const Reports: React.FC = () => {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('1');
  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState<any[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  // Filtreler
  const [startDate, setStartDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedSite, setSelectedSite] = useState('');

  // Rapor verileri
  const [waybillReport, setWaybillReport] = useState<WaybillReport | null>(null);
  const [wageReport, setWageReport] = useState<WageReport | null>(null);
  const [dailyReport, setDailyReport] = useState<DailyReport | null>(null);

  React.useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      const response = await projectsAPI.getSites();
      setSites(response.data);
    } catch (error) {
      console.error('Sites fetch error:', error);
    }
  };

  const fetchWaybillReport = async () => {
    try {
      setLoading(true);
      const response = await reportsAPI.getWaybillMatch(startDate, endDate, selectedSite || undefined);
      setWaybillReport(response.data);
    } catch (error: any) {
      toast.error('Rapor alınamadı');
    } finally {
      setLoading(false);
    }
  };

  const fetchWageReport = async () => {
    if (!selectedSite) {
      toast.error('Lütfen şantiye seçin');
      return;
    }
    try {
      setLoading(true);
      const response = await reportsAPI.getMonthlyWages(
        selectedSite,
        new Date(startDate).getMonth() + 1,
        new Date(startDate).getFullYear()
      );
      setWageReport(response.data);
    } catch (error: any) {
      toast.error('Rapor alınamadı');
    } finally {
      setLoading(false);
    }
  };

  const fetchDailyReport = async () => {
    if (!selectedSite) {
      toast.error('Lütfen şantiye seçin');
      return;
    }
    try {
      setLoading(true);
      const response = await reportsAPI.getDailySummary(selectedSite, startDate, endDate);
      setDailyReport(response.data);
    } catch (error: any) {
      toast.error('Rapor alınamadı');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = () => {
    switch (activeTab) {
      case '1':
        fetchWaybillReport();
        break;
      case '2':
        fetchWageReport();
        break;
      case '3':
        fetchDailyReport();
        break;
    }
  };

  const hasActiveFilters = selectedSite !== '' ||
    startDate !== new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0] ||
    endDate !== new Date().toISOString().split('T')[0];

  const activeFilterCount = [selectedSite].filter(Boolean).length +
    (startDate !== new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0] ||
     endDate !== new Date().toISOString().split('T')[0] ? 1 : 0);

  return (
    <>
      <Row>
        <Col xs={12}>
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">Raporlar</h4>
          </div>
        </Col>
      </Row>

      {/* Filtreler */}
      <Row>
        <Col lg={12}>
          <Card>
            <CardHeader>
              <h5 className="card-title mb-0">Rapor Filtreleri</h5>
            </CardHeader>
            <CardBody>
              {/* Desktop Filters */}
              {!isMobile && (
                <Form>
                  <Row>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Başlangıç Tarihi</Label>
                        <Input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Bitiş Tarihi</Label>
                        <Input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Şantiye</Label>
                        <Input
                          type="select"
                          value={selectedSite}
                          onChange={(e) => setSelectedSite(e.target.value)}
                        >
                          <option value="">Tüm Şantiyeler</option>
                          {sites.map((site) => (
                            <option key={site.id} value={site.id}>
                              {site.project.name} / {site.name}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={2} className="d-flex align-items-end">
                      <Button
                        color="primary"
                        className="w-100 mb-3"
                        onClick={handleGenerateReport}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1"></span>
                            Yükleniyor...
                          </>
                        ) : (
                          <>
                            <i className="ri-file-chart-line me-1"></i>
                            Rapor Al
                          </>
                        )}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              )}

              {/* Mobile Filters */}
              {isMobile && (
                <>
                  <button
                    className="btn btn-sm btn-light w-100 mb-3 d-flex align-items-center justify-content-between"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <span>
                      <i className="ri-filter-3-line me-2"></i>
                      Filtreler
                      {hasActiveFilters && (
                        <Badge color="primary" className="ms-2">
                          {activeFilterCount}
                        </Badge>
                      )}
                    </span>
                    <i className={`ri-arrow-${showFilters ? 'up' : 'down'}-s-line`}></i>
                  </button>

                  <Collapse isOpen={showFilters}>
                    <div className="bg-light p-3 rounded mb-3">
                      <div className="mb-2">
                        <label className="small text-muted mb-1">Başlangıç Tarihi</label>
                        <Input
                          type="date"
                          bsSize="sm"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="small text-muted mb-1">Bitiş Tarihi</label>
                        <Input
                          type="date"
                          bsSize="sm"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="small text-muted mb-1">Şantiye</label>
                        <Input
                          type="select"
                          bsSize="sm"
                          value={selectedSite}
                          onChange={(e) => setSelectedSite(e.target.value)}
                        >
                          <option value="">Tüm Şantiyeler</option>
                          {sites.map((site) => (
                            <option key={site.id} value={site.id}>
                              {site.project.name} / {site.name}
                            </option>
                          ))}
                        </Input>
                      </div>
                    </div>
                  </Collapse>

                  <Button
                    color="primary"
                    className="w-100 mb-2"
                    onClick={handleGenerateReport}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1"></span>
                        Yükleniyor...
                      </>
                    ) : (
                      <>
                        <i className="ri-file-chart-line me-1"></i>
                        Rapor Al
                      </>
                    )}
                  </Button>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

        {/* Rapor Sekmeleri */}
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <Nav tabs className="nav-tabs-custom nav-primary">
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === '1' })}
                      onClick={() => setActiveTab('1')}
                      style={{ cursor: 'pointer' }}
                    >
                      <i className="ri-file-list-3-line me-1"></i>
                      İrsaliye Eşleşme
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === '2' })}
                      onClick={() => setActiveTab('2')}
                      style={{ cursor: 'pointer' }}
                    >
                      <i className="ri-money-dollar-circle-line me-1"></i>
                      Yevmiye Raporu
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === '3' })}
                      onClick={() => setActiveTab('3')}
                      style={{ cursor: 'pointer' }}
                    >
                      <i className="ri-calendar-check-line me-1"></i>
                      Günlük Özet
                    </NavLink>
                  </NavItem>
                </Nav>

                <TabContent activeTab={activeTab} className="pt-4">
                  {/* İrsaliye Eşleşme Raporu */}
                  <TabPane tabId="1">
                    {waybillReport ? (
                      <Row>
                        <Col md={3}>
                          <Card className="bg-primary-subtle">
                            <CardBody className="text-center">
                              <h3 className="text-primary">{waybillReport.total}</h3>
                              <p className="text-muted mb-0">Toplam İrsaliye</p>
                            </CardBody>
                          </Card>
                        </Col>
                        <Col md={3}>
                          <Card className="bg-success-subtle">
                            <CardBody className="text-center">
                              <h3 className="text-success">{waybillReport.matched}</h3>
                              <p className="text-muted mb-0">Eşleşen</p>
                            </CardBody>
                          </Card>
                        </Col>
                        <Col md={3}>
                          <Card className="bg-warning-subtle">
                            <CardBody className="text-center">
                              <h3 className="text-warning">{waybillReport.pending}</h3>
                              <p className="text-muted mb-0">Bekleyen</p>
                            </CardBody>
                          </Card>
                        </Col>
                        <Col md={3}>
                          <Card className="bg-danger-subtle">
                            <CardBody className="text-center">
                              <h3 className="text-danger">{waybillReport.notArrived}</h3>
                              <p className="text-muted mb-0">Gelmedi</p>
                            </CardBody>
                          </Card>
                        </Col>
                        <Col xs={12}>
                          <Alert color="info" className="mt-3">
                            <strong>Eşleşme Oranı:</strong> %{waybillReport.matchRate.toFixed(1)}
                          </Alert>
                        </Col>
                      </Row>
                    ) : (
                      <div className="text-center py-4 text-muted">
                        Rapor almak için filtreleri seçin ve "Rapor Al" butonuna tıklayın.
                      </div>
                    )}
                  </TabPane>

                  {/* Yevmiye Raporu */}
                  <TabPane tabId="2">
                    {wageReport ? (
                      <>
                        <Row className="mb-3">
                          <Col md={6}>
                            <Card className="bg-success-subtle">
                              <CardBody>
                                <h4 className="text-success">
                                  ₺{wageReport.totalWage.toLocaleString('tr-TR')}
                                </h4>
                                <p className="text-muted mb-0">Toplam Yevmiye</p>
                              </CardBody>
                            </Card>
                          </Col>
                          <Col md={6}>
                            <Card className="bg-info-subtle">
                              <CardBody>
                                <h4 className="text-info">{wageReport.totalDays}</h4>
                                <p className="text-muted mb-0">Toplam İş Günü</p>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                        {isMobile ? (
                          <div className="admin-card-list">
                            {wageReport.personnel.map((person) => (
                              <div key={person.id} className="admin-card-item">
                                <div className="admin-card-header">
                                  <h6 className="admin-card-title">
                                    {person.firstName} {person.lastName}
                                  </h6>
                                  <Badge color="success">
                                    ₺{person.totalWage.toLocaleString('tr-TR')}
                                  </Badge>
                                </div>
                                <div className="admin-card-body">
                                  <div className="admin-info-row">
                                    <span className="admin-info-label">
                                      <i className="ri-briefcase-line"></i>
                                      Ünvan
                                    </span>
                                    <span className="admin-info-value">{person.title || 'İşçi'}</span>
                                  </div>
                                  <div className="admin-info-row">
                                    <span className="admin-info-label">
                                      <i className="ri-calendar-check-line"></i>
                                      Gün Sayısı
                                    </span>
                                    <span className="admin-info-value">{person.totalDays} gün</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="table-responsive">
                            <Table className="table-hover">
                              <thead>
                                <tr>
                                  <th>Personel</th>
                                  <th>Ünvan</th>
                                  <th>Gün Sayısı</th>
                                  <th>Toplam Yevmiye</th>
                                </tr>
                              </thead>
                              <tbody>
                                {wageReport.personnel.map((person) => (
                                  <tr key={person.id}>
                                    <td>
                                      <strong>
                                        {person.firstName} {person.lastName}
                                      </strong>
                                    </td>
                                    <td>{person.title || 'İşçi'}</td>
                                    <td>{person.totalDays}</td>
                                    <td>₺{person.totalWage.toLocaleString('tr-TR')}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-4 text-muted">
                        {selectedSite
                          ? 'Rapor almak için "Rapor Al" butonuna tıklayın.'
                          : 'Yevmiye raporu için şantiye seçimi zorunludur.'}
                      </div>
                    )}
                  </TabPane>

                  {/* Günlük Özet */}
                  <TabPane tabId="3">
                    {dailyReport ? (
                      isMobile ? (
                        <div className="admin-card-list">
                          {dailyReport.records.map((record) => (
                            <div key={record.id} className="admin-card-item">
                              <div className="admin-card-header">
                                <h6 className="admin-card-title">
                                  {new Date(record.date).toLocaleDateString('tr-TR')}
                                </h6>
                                <Badge color="primary">{record.siteName}</Badge>
                              </div>
                              <div className="admin-card-body">
                                <div className="admin-info-row">
                                  <span className="admin-info-label">
                                    <i className="ri-team-line"></i>
                                    Personel
                                  </span>
                                  <span className="admin-info-value">{record.personnelCount} kişi</span>
                                </div>
                                <div className="admin-info-row">
                                  <span className="admin-info-label">
                                    <i className="ri-truck-line"></i>
                                    Araç
                                  </span>
                                  <span className="admin-info-value">{record.vehicleCount} araç</span>
                                </div>
                                <div className="admin-info-row">
                                  <span className="admin-info-label">
                                    <i className="ri-hammer-line"></i>
                                    İş
                                  </span>
                                  <span className="admin-info-value">{record.workCount} iş</span>
                                </div>
                                <div className="admin-info-row">
                                  <span className="admin-info-label">
                                    <i className="ri-user-line"></i>
                                    Oluşturan
                                  </span>
                                  <span className="admin-info-value">{record.createdBy}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <Table className="table-hover">
                            <thead>
                              <tr>
                                <th>Tarih</th>
                                <th>Şantiye</th>
                                <th>Personel</th>
                                <th>Araç</th>
                                <th>İş</th>
                                <th>Oluşturan</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dailyReport.records.map((record) => (
                                <tr key={record.id}>
                                  <td>
                                    <strong>
                                      {new Date(record.date).toLocaleDateString('tr-TR')}
                                    </strong>
                                  </td>
                                  <td>{record.siteName}</td>
                                  <td>{record.personnelCount} kişi</td>
                                  <td>{record.vehicleCount} araç</td>
                                  <td>{record.workCount} iş</td>
                                  <td>{record.createdBy}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )
                    ) : (
                      <div className="text-center py-4 text-muted">
                        {selectedSite
                          ? 'Rapor almak için "Rapor Al" butonuna tıklayın.'
                          : 'Günlük özet raporu için şantiye seçimi zorunludur.'}
                      </div>
                    )}
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
      </Row>
    </>
  );
};

export default Reports;
